'use client';

import { useState, useEffect, useCallback } from 'react';
import { savedOutfitsService, type SavedOutfit } from '@/lib/services/savedOutfitsService';
import type { OutfitData, WizardData } from '@/app/fashion-discovery-interface/lib/types';
import { createClient } from '@/lib/supabase/client';

export function useSavedOutfits() {
  const [savedOutfits, setSavedOutfits] = useState<SavedOutfit[]>([]);
  const [loading, setLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const supabase = createClient();
    supabase.auth.getUser().then(({ data: { user } }) => {
      setIsAuthenticated(!!user);
      if (user) {
        loadSavedOutfits();
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      const authed = !!session?.user;
      setIsAuthenticated(authed);
      if (authed) {
        loadSavedOutfits();
      } else {
        setSavedOutfits([]);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const loadSavedOutfits = useCallback(async () => {
    setLoading(true);
    try {
      const data = await savedOutfitsService.getAll();
      setSavedOutfits(data);
    } catch {
      // silently fail — storage is optional
    } finally {
      setLoading(false);
    }
  }, []);

  const toggleBookmark = useCallback(
    async (outfit: OutfitData, wizardData: WizardData): Promise<boolean> => {
      if (!isAuthenticated) return false;

      const existing = savedOutfits.find(
        (s) => s.outfit.editorialTitle === outfit.editorialTitle
      );

      if (existing) {
        // Remove
        const ok = await savedOutfitsService.remove(existing.id);
        if (ok) {
          setSavedOutfits((prev) => prev.filter((s) => s.id !== existing.id));
        }
        return false; // now unsaved
      } else {
        // Save
        const saved = await savedOutfitsService.save(outfit, wizardData);
        if (saved) {
          setSavedOutfits((prev) => [saved, ...prev]);
        }
        return true; // now saved
      }
    },
    [isAuthenticated, savedOutfits]
  );

  const isSaved = useCallback(
    (outfit: OutfitData): boolean => {
      return savedOutfits.some((s) => s.outfit.editorialTitle === outfit.editorialTitle);
    },
    [savedOutfits]
  );

  return {
    savedOutfits,
    loading,
    isAuthenticated,
    toggleBookmark,
    isSaved,
    reload: loadSavedOutfits,
  };
}
